export function init() {
  console.log('[initialized] main-nav');
  const mainNav = document.querySelector('.main-nav');
  const mainNavWrapper = document.querySelector('.header__nav__wrapper');
  const isDesktop = window.matchMedia('(min-width: 992px)').matches;
  const header = document.querySelector('.header');
  const MAX_ITEMS_PER_COLUMN = 8;
  let currentSecondLevelHeadlineLink: string | null = null;

  if (!mainNav || !header || !mainNavWrapper) return;

  const mainNavButtons = Array.from(mainNav.querySelectorAll('button')).filter(
    (button) => button.hasAttribute('aria-controls'),
  );

  const backButtons = Array.from(
    mainNav.querySelectorAll('.main-nav__back-button'),
  );

  /**
   * Resets the active item and hides the associated sub-menu.
   * @param activeItem - The active item element.
   */
  const resetActiveItem = (activeItem: Element) => {
    const button = activeItem.querySelector('button');
    if (!activeItem || !button) return;
    activeItem.classList.remove('sub-menu-active');
    const activeSubMenuId = button?.getAttribute('aria-controls');
    const activeSubMenu = activeItem.querySelector('#' + activeSubMenuId);
    if (!activeSubMenu) return;
    activeSubMenu.classList.remove('is-active');
    activeSubMenu.setAttribute('aria-hidden', 'true');
    button.setAttribute('aria-expanded', 'false');

    toggleTeaserVisibility(activeSubMenu, false);
    toggleVisibilityOfOtherColumns(activeSubMenu, false);
  };

  /**
   * Resets the active state of the first level items in the main navigation.
   */
  const resetActiveFirstLevel = () => {
    const activeItems = mainNav.querySelectorAll(
      '.main-nav__level-one__item.sub-menu-active',
    );
    activeItems?.forEach((item) => resetActiveItem(item));
  };

  /**
   * Toggles the scroll behavior of the navigation wrapper via the 'no-scroll' class.
   * This function has no effect on desktop devices.
   *
   * @param button - Optional button element to trigger the scroll behavior.
   */
  const toggleNavWrapperScroll = (button?: HTMLButtonElement) => {
    const navWrapper = document.querySelector('.header__nav__wrapper ');
    if (!navWrapper || isDesktop) return;

    if (!button && navWrapper.classList.contains('no-scroll')) {
      navWrapper.classList.remove('no-scroll');
      return;
    } else if (button) {
      navWrapper.classList.toggle('no-scroll');
      if (navWrapper.classList.contains('no-scroll')) {
        navWrapper.scrollTop = 0;
      }
    }
  };

  /**
   * Resets the active second level items in the main navigation.
   * It finds the active flyout element and resets all the active items within it.
   */
  const resetActiveSecondLevel = () => {
    const activeFlyout = mainNav.querySelector(
      '.main-nav__flyout[aria-hidden="false"]',
    );
    if (!activeFlyout) return;
    const activeItems = activeFlyout.querySelectorAll(
      '.has-sub-menu.sub-menu-active.main-nav__level-two__item',
    );
    activeItems?.forEach((item) => resetActiveItem(item));
  };

  /**
   * Resets the active state of the third level items in the main navigation.
   * It finds all the active third level items with sub-menus and resets their active state.
   */
  const resetActiveThirdLevel = () => {
    const activeItems = mainNav.querySelectorAll(
      '.main-nav__level-three__item.has-sub-menu.sub-menu-active',
    );
    activeItems?.forEach((item) => resetActiveItem(item));
  };

  /**
   * Gets the index of the parent element in the second level.
   * @param subMenu - The element representing the submenu.
   * @returns The index of the parent element in the second level.
   */
  const getParentIndex = (subMenu: Element): number | null => {
    const levelTwo = subMenu.closest('.main-nav__level-two__sub-menu-wrapper');
    const parent = subMenu.parentElement as Element;
    const parentIsSecondLevel = parent?.classList.contains(
      'main-nav__level-two__item',
    );

    if (!parentIsSecondLevel || !levelTwo) return null;

    const secondLevelList = levelTwo.querySelector('ul');
    return Array.from(
      secondLevelList?.querySelectorAll('.main-nav__level-two__item') || [],
    )
      .filter(
        (item: Element) =>
          // Exclude the parent link from the count
          !item.classList.contains('main-nav__sub-menu__headline__parent-link'),
      )
      .indexOf(parent);
  };

  /**
   * Checks if there item clicked is in the second column.
   * @param subMenu - The element representing the submenu.
   * @returns A boolean indicating whether the item is in the second column.
   */
  const isSecondColumnOfLevelTwo = (subMenu: Element) => {
    const indexOfParent = getParentIndex(subMenu);
    if (!indexOfParent) return false;

    return (
      indexOfParent + 1 > MAX_ITEMS_PER_COLUMN &&
      indexOfParent + 1 <= MAX_ITEMS_PER_COLUMN * 2
    );
  };

  /**
   * Checks if there item clicked is in the third column.
   * @param subMenu - The element representing the submenu.
   * @returns A boolean indicating whether the item is in the third column.
   */
  const isThirdColumnOfLevelTwo = (subMenu: Element) => {
    const indexOfParent = getParentIndex(subMenu);

    if (!indexOfParent) return false;

    return indexOfParent >= MAX_ITEMS_PER_COLUMN * 2;
  };

  const hideSecondColumn = (subMenu: Element, shouldHide: boolean) => {
    hideColumn(subMenu, false, shouldHide);
    return;
  };

  const hideThirdColumn = (subMenu: Element, shouldHide: boolean) => {
    hideColumn(subMenu, true, shouldHide);
    return;
  };

  /**
   * Toggles the visibility of the second and / or third column of second level items.
   * @param subMenu - The element representing the submenu.
   * @param hideThirdColumn - A boolean indicating whether the item third column should be hidden.
   * @param shouldHide - A boolean indicating whether the column should be hidden or not.
   */
  const hideColumn = (
    subMenu: Element,
    hideThirdColumn: boolean,
    shouldHide: boolean,
  ) => {
    const levelTwo = subMenu.closest('.main-nav__level-two__sub-menu-wrapper');

    if (!levelTwo) return;

    const levelTwoItems = Array.from(
      levelTwo.querySelectorAll('.main-nav__level-two__item'),
    );
    const firstIndexInSecondColumn = MAX_ITEMS_PER_COLUMN + 1;
    const firstIndexInThirdColumn = MAX_ITEMS_PER_COLUMN * 2 + 1;

    levelTwoItems
      .slice(
        hideThirdColumn ? firstIndexInThirdColumn : firstIndexInSecondColumn,
      )
      .filter(
        (item: Element) =>
          // Exclude the parent link from the count
          !item.classList.contains('main-nav__sub-menu__headline__parent-link'),
      )
      .forEach((item: Element) => {
        item.classList.toggle('is-hidden', shouldHide);
      });
  };

  const toggleBackButton = (submenu: Element) => {
    const secondLevel = submenu.closest('.main-nav__level-two');
    const levelBackButton = secondLevel?.closest('.main-nav__level-two__sub-menu-wrapper')?.querySelector('.main-nav__back-button');
    if (!levelBackButton) return;
    if (levelBackButton.classList.contains('is-visible')) {
      levelBackButton.classList.remove('is-visible');
    } else {
      const backButtonSpan = levelBackButton.querySelector('span');
      if (backButtonSpan) {
        if (!isDesktop) {
          // Check if we're in a level-2 view by looking for active level-2 items
          const activeLevelTwoItem = document.querySelector('.main-nav__level-two__item.has-sub-menu.sub-menu-active');
          if (activeLevelTwoItem) {
            // We're in level-2 view, show just "Zurück" (for mobile view)
            backButtonSpan.textContent = 'Zurück';
          } else {
            // We're in level-3 view, show "Zurück zu [title]"
            const firstLevelTitle = document.querySelector('.main-nav__level-one__item.has-sub-menu.sub-menu-active')?.querySelector('button[aria-expanded="true"]')?.textContent?.trim() || '';
            backButtonSpan.textContent = `Zurück zu ${firstLevelTitle}`;
          }
        } else {
          changeTextContentOfBackButton(levelBackButton);
        }
      }
      levelBackButton.classList.add('is-visible');
    }
  }

  const shiftMenuToTheLeftOnce = (submenu: Element) => {
    const secondLevel = submenu.closest('.main-nav__level-two');
    if (!secondLevel) return;
    if (secondLevel.classList.contains('translate-left-once')) {
      secondLevel.classList.remove('translate-left-once');
    } else {
      secondLevel.classList.add('translate-left-once');
    };
  }

  const shiftMenuToTheLeftTwice = (submenu: Element) => {
    const secondLevel = submenu.closest('.main-nav__level-two');
    if (!secondLevel) return;
    if (secondLevel.classList.contains('translate-left-twice')) {
      secondLevel.classList.remove('translate-left-twice');
    } else {
      secondLevel.classList.add('translate-left-twice');
    }
  }

  const shiftMenuToTheLeftThreeTimes = (submenu: Element) => {
    const secondLevel = submenu.closest('.main-nav__level-two');
    if (!secondLevel) return;
    if (secondLevel.classList.contains('translate-left-three-times')) {
      secondLevel.classList.remove('translate-left-three-times');
    } else {
      secondLevel.classList.add('translate-left-three-times');
    }
  }
  /**
   * Modifies the text content of a back button to include the second-level menu title.
   * If a <span> element is already present, it updates the text; otherwise, it creates a new <span>.
   * 
   * @param button - The button element whose text content should be changed.
   */
  const changeTextContentOfBackButton = (button: Element) => {
    const firstLevelTitle = document.querySelector('.main-nav__level-one__item.has-sub-menu.sub-menu-active')?.querySelector('button[aria-expanded="true"]')?.textContent?.trim() || '';
    if (!button) return;
    const span = button.querySelector('span');
    if (span) {
      span.textContent = `Zurück zu ${firstLevelTitle}`;
    }
  };

  const getLinkOfSecondLevelHeadline = (submenu: Element) => {
    const secondLevelLink = submenu.querySelector('.main-nav__sub-menu__headline')?.getAttribute('href') as string;
    return secondLevelLink || '';
  }

  /**
   * Updates the title of the second-level submenu based on 
   * its active state and the active third-level submenu.
   * 
   * @param submenu - The submenu element to inspect for determining and setting the new title.
   */
  const changeSubmenuTitle = (submenu: Element) => {
    const secondLevel = submenu.closest('.main-nav__level-two');
    if (!secondLevel) return;
    const secondLevelWrapper = secondLevel.closest('.main-nav__level-two__sub-menu-wrapper');
    const secondLevelTitleElement = secondLevelWrapper && secondLevelWrapper.querySelector('.main-nav__sub-menu__headline');
    if (!secondLevelTitleElement) {
      return
    }

    // Helper function to get href from parent link
    const getHrefFromParentLink = (wrapper: Element | null) => {
      if (!wrapper) return '';
      const parentLink = wrapper.querySelector('.main-nav__level-three__item.main-nav__sub-menu__headline__parent-link a');
      return parentLink ? parentLink.getAttribute('href') || '' : '';
    };

    if (secondLevel.classList.contains('translate-left-twice') || secondLevel.classList.contains('translate-left-once')) {
      const activeSecondLevelItem = document.querySelector('.main-nav__level-two__item.has-sub-menu.sub-menu-active');
      if (activeSecondLevelItem) {
        const button = activeSecondLevelItem.querySelector('button[aria-expanded="true"]');
        if (button) {
          secondLevelTitleElement.textContent = button.textContent?.trim() || '';
          const subMenuId = button.getAttribute('aria-controls');
          if (subMenuId) {
            const subMenu = activeSecondLevelItem.querySelector('#' + subMenuId);
            const href = getHrefFromParentLink(subMenu);
            if (href) {
              secondLevelTitleElement.setAttribute('href', href);
            }
          }
        }
      }
    } else {
      const activeSecondLevelItem = submenu.closest('.main-nav__level-two__item.has-sub-menu.sub-menu-active');
      if (activeSecondLevelItem) {
        const button = activeSecondLevelItem.querySelector('button[aria-expanded="true"]');
        if (button) {
          secondLevelTitleElement.textContent = button.textContent?.trim() || '';
          const subMenuId = button.getAttribute('aria-controls');
          if (subMenuId) {
            const subMenu = activeSecondLevelItem.querySelector('#' + subMenuId);
            const href = getHrefFromParentLink(subMenu);
            if (href) {
              secondLevelTitleElement.setAttribute('href', href);
            }
          }
        }
      } else {
        const thirdLevelWrapper = submenu.querySelector('.main-nav__level-three__sub-menu-wrapper');
        if (!thirdLevelWrapper) return;

        const thirdLevelTitle = thirdLevelWrapper.querySelector('.main-nav__sub-menu__headline')?.textContent;
        if (thirdLevelTitle) {
          secondLevelTitleElement.textContent = thirdLevelTitle;
        }

        const href = getHrefFromParentLink(thirdLevelWrapper);
        if (href) {
          secondLevelTitleElement.setAttribute('href', href);
        }
      }
    }
  }

  /**
   * Hides the second column of the second level if it has more than 10 items.
   * Second level items over 10 are wrapped in a second column.
   * @param subMenu - The element representing the submenu.
   * @param shouldHide - A boolean indicating whether the second column should be hidden or not.
   */
  const toggleVisibilityOfOtherColumns = (
    subMenu: Element,
    shouldHide: boolean,
  ) => {
    const amountOfThirdLevelItems = Array.from(subMenu.querySelectorAll('.main-nav__level-three__item')).length;
    let toggleBackButtonAndTitle = false;
    if (isThirdColumnOfLevelTwo(subMenu)) {
      toggleBackButtonAndTitle = true;
      if (amountOfThirdLevelItems > MAX_ITEMS_PER_COLUMN * 2) {
        shiftMenuToTheLeftThreeTimes(subMenu);
      } else {
        shiftMenuToTheLeftTwice(subMenu);
      }
    }

    else if (isSecondColumnOfLevelTwo(subMenu)) {
      const levelTwo = subMenu.closest('.main-nav__level-two__sub-menu-wrapper');
      const levelTwoItems = Array.from(levelTwo?.querySelectorAll('.main-nav__level-two__item:not(.main-nav__sub-menu__headline__parent-link)') || []);
      const levelTwoHasThreeColumns = levelTwoItems.length > MAX_ITEMS_PER_COLUMN * 2;
      if (levelTwoHasThreeColumns) {
        hideThirdColumn(subMenu, shouldHide);
      }
      if (amountOfThirdLevelItems > MAX_ITEMS_PER_COLUMN * 3) {
        toggleBackButtonAndTitle = true;
        shiftMenuToTheLeftTwice(subMenu);
      }
      else if (amountOfThirdLevelItems > MAX_ITEMS_PER_COLUMN * 2) {
        toggleBackButtonAndTitle = true;
        shiftMenuToTheLeftOnce(subMenu);
      }
    } else {
      hideSecondColumn(subMenu, shouldHide);
      if (amountOfThirdLevelItems > MAX_ITEMS_PER_COLUMN * 3) {
        toggleBackButtonAndTitle = true;
        shiftMenuToTheLeftOnce(subMenu);
      }
    }

    if (toggleBackButtonAndTitle) {
      changeSubmenuTitle(subMenu)
      toggleBackButton(subMenu)
    }
  };


  /**
   * Toggles the visibility of the teaser in the main navigation submenu.
   * If the level three submenu has more than five items, or level two has more than 10 items
   * and there is more than one teaser, the first teaser is hidden to make space for the rest
   * of the items.
   *
   * @param subMenu - The element representing the submenu.
   * @param isActive - A boolean indicating whether the teaser should be active or not.
   */
  const toggleTeaserVisibility = (subMenu: Element, isActive: boolean) => {
    const isLevelThree = subMenu.classList.contains(
      'main-nav__level-three__sub-menu-wrapper',
    );
    const flyout = subMenu.closest('.main-nav__flyout');
    if (!flyout) return;
    const teaserWrapper = flyout.querySelector(
      '.main-nav__level-two__teaser-wrapper',
    );

    if (!teaserWrapper || !isLevelThree) return;
    const teasers = teaserWrapper.querySelectorAll(
      '.main-nav__level-two__teaser',
    );
    const levelThreeItemsCount = Array.from(
      subMenu.querySelectorAll(
        `.main-nav__level-three__item`,
      ),
    ).filter((e) => e).length;

    const levelTwo = subMenu.closest('.main-nav__level-two__sub-menu-wrapper');
    if (!levelTwo) return;

    const isSecondColumn = isSecondColumnOfLevelTwo(subMenu);
    const isThirdColumn = isThirdColumnOfLevelTwo(subMenu);
    const hideFirstTeaser = (isSecondColumn && levelThreeItemsCount > 0) || (!isSecondColumn && !isThirdColumn && levelThreeItemsCount > MAX_ITEMS_PER_COLUMN);
    const hideBothTeasers = (levelThreeItemsCount > MAX_ITEMS_PER_COLUMN * 2) || (isSecondColumn && levelThreeItemsCount > MAX_ITEMS_PER_COLUMN);

    if (hideBothTeasers) {
      teasers.forEach((teaser) => {
        teaser.setAttribute('aria-hidden', isActive ? 'true' : 'false');
        teaser.classList.toggle('is-hidden', isActive);
      })
    } else if (hideFirstTeaser) {
      const firstTeaser = Array.from(teasers)[0];
      firstTeaser.setAttribute('aria-hidden', isActive ? 'true' : 'false');
      firstTeaser.classList.toggle('is-hidden', isActive);
    }
  };

  /**
   * Toggles the visibility of a submenu based on the button clicked.
   * @param button - The HTML button element that triggered the submenu toggle.
   */
  const toggleSubMenuVisibility = (button: HTMLButtonElement) => {
    const parent = button.closest('li.has-sub-menu');
    if (!parent) return;
    parent.classList.toggle('sub-menu-active');
    const subMenuId = button.getAttribute('aria-controls');
    const subMenu = parent.querySelector('#' + subMenuId);

    if (!subMenu) return;

    subMenu.classList.toggle('is-active');
    // Set aria attributes
    const isActive = subMenu.classList.contains('is-active');
    const firstLevelClicked = parent.classList.contains(
      'main-nav__level-one__item',
    );
    if (!isDesktop && firstLevelClicked) {
      toggleNavWrapperScroll(button);
    }

    if (firstLevelClicked) {
      currentSecondLevelHeadlineLink = getLinkOfSecondLevelHeadline(parent);
    }

    button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    subMenu.setAttribute('aria-hidden', isActive ? 'false' : 'true');

    // Check if second column of second level needs to be hidden
    toggleVisibilityOfOtherColumns(subMenu, isActive);
    // Check if teaser needs to be hidden
    toggleTeaserVisibility(subMenu, isActive);

    // Update back button text for mobile
    if (!isDesktop) {
      const backButton = subMenu.querySelector('.main-nav__back-button');
      if (backButton) {
        const backButtonSpan = backButton.querySelector('span');
        if (backButtonSpan) {
          if (firstLevelClicked) {
            backButtonSpan.textContent = 'Zurück';
          } else {
            const firstLevelTitle = document.querySelector('.main-nav__level-one__item.has-sub-menu.sub-menu-active')?.querySelector('button[aria-expanded="true"]')?.textContent?.trim() || '';
            backButtonSpan.textContent = `Zurück zu ${firstLevelTitle}`;
          }
        }
      }

      // Set the correct href for the headline in mobile view
      if (!firstLevelClicked && isActive) {
        const headline = subMenu.querySelector('.main-nav__sub-menu__headline');
        if (headline) {
          const parentLink = subMenu.querySelector('.main-nav__level-three__item.main-nav__sub-menu__headline__parent-link a');
          if (parentLink) {
            const href = parentLink.getAttribute('href');
            if (href) {
              headline.setAttribute('href', href);
            }
          }
        }
      }
    }
  };

  const itemIsFirstLevel = (item: Element): boolean => {
    return item.classList.contains('main-nav__level-one__item');
  };

  const itemIsSecondLevel = (item: Element): boolean => {
    return item.classList.contains('main-nav__level-one__item');
  };

  /**
   * Toggles the submenu visibility based on the provided button element.
   * If the parent element of the button has the class 'sub-menu-active', it will reset the active item and toggle the navigation wrapper scroll.
   * If the parent element is a first-level item or the button is not the same as the previously clicked button, it will reset the active second-level items.
   * If the parent element is a second-level item, it will reset the active third-level items.
   * @param button - The HTML button element that triggers the submenu toggle.
   */
  const toggleSubmenu = (button: HTMLButtonElement) => {
    const parent = button.closest('li.has-sub-menu');
    if (!parent) return;

    const firstLevelClicked = itemIsFirstLevel(parent);
    const secondLevelClicked = itemIsSecondLevel(parent);

    const sameButtonClicked = button.getAttribute('aria-expanded') === 'true';
    if ((isDesktop && !sameButtonClicked) || firstLevelClicked) {
      resetActiveSecondLevel();
    } else if (secondLevelClicked) {
      resetActiveThirdLevel();
    }

    if (parent.classList.contains('sub-menu-active')) {
      resetActiveItem(parent);

      if (!isDesktop && firstLevelClicked) {
        toggleNavWrapperScroll(button);
      }
      return;
    }

    toggleSubMenuVisibility(button);
  };

  /**
   * Handles the click event of a navigation button.
   * @param clickedButton - The button element that was clicked.
   */
  const onNavButtonClicked = (clickedButton: HTMLButtonElement) => {
    if (!clickedButton) return;
    const buttonIsFirstLevel = clickedButton.parentElement?.classList.contains(
      'main-nav__level-one__item',
    );
    const existingActiveButton = mainNav.querySelector(
      'li.main-nav__level-one__item > button[aria-expanded="true"]',
    ) as HTMLButtonElement;

    toggleSubmenu(clickedButton);

    // A first level button was clicked and there is another active button
    if (
      buttonIsFirstLevel &&
      existingActiveButton &&
      existingActiveButton !== clickedButton
    ) {
      // Hide the other active submenu after a short delay
      setTimeout(() => {
        toggleSubmenu(existingActiveButton);
      }, 50);
    }
  };

  const onDesktopBackButtonClicked = (clickedButton: HTMLButtonElement) => {
    const parent = clickedButton.parentElement;
    const isSecondLevel = parent?.classList.contains('main-nav__level-two__sub-menu-wrapper');
    if (isSecondLevel) {
      const submenu = parent?.querySelector('.main-nav__level-two');
      const activeThirdLevelSubmenu = submenu?.querySelector('.main-nav__level-two__item.has-sub-menu.sub-menu-active');
      if (activeThirdLevelSubmenu) {
        const activeThirdLevelButton = activeThirdLevelSubmenu?.querySelector('button[aria-expanded="true"]') as HTMLButtonElement;
        toggleSubMenuVisibility(activeThirdLevelButton);

        // Restore the second level headline
        const secondLevelHeadline = parent?.querySelector('.main-nav__sub-menu__headline');
        if (secondLevelHeadline) {
          const firstLevelItem = document.querySelector('.main-nav__level-one__item.has-sub-menu.sub-menu-active');
          const firstLevelButton = firstLevelItem?.querySelector('button[aria-expanded="true"]');
          if (firstLevelButton) {
            secondLevelHeadline.textContent = firstLevelButton.textContent?.trim() || '';
            secondLevelHeadline.setAttribute('href', firstLevelItem?.querySelector('.main-nav__sub-menu__headline__parent-link a')?.getAttribute('href') || '');
            secondLevelHeadline.setAttribute('aria-hidden', 'false');
            secondLevelHeadline.classList.remove('is-hidden');
          }
        }
      }
    }
  }

  const onBackButtonClicked = (clickedButton: HTMLButtonElement) => {
    if (isDesktop) {
      onDesktopBackButtonClicked(clickedButton);
    } else {
      const parent = clickedButton.closest('li.has-sub-menu.sub-menu-active');
      if (parent) {
        const parentButton = parent.querySelector('button') as HTMLButtonElement;
        if (parentButton) {
          toggleSubMenuVisibility(parentButton);

          // Restore the second level headline for mobile
          const secondLevelHeadline = parent.closest('.main-nav__level-two__sub-menu-wrapper')?.querySelector('.main-nav__sub-menu__headline');
          if (secondLevelHeadline) {
            const firstLevelItem = document.querySelector('.main-nav__level-one__item.has-sub-menu.sub-menu-active');
            const firstLevelButton = firstLevelItem?.querySelector('button[aria-expanded="true"]');
            if (firstLevelButton) {
              secondLevelHeadline.textContent = firstLevelButton.textContent?.trim() || '';
              secondLevelHeadline.setAttribute('href', firstLevelItem?.querySelector('.main-nav__sub-menu__headline__parent-link a')?.getAttribute('href') || '');
              secondLevelHeadline.setAttribute('aria-hidden', 'false');
              secondLevelHeadline.classList.remove('is-hidden');
            }
          }

          // Update back button text for mobile
          const backButtonSpan = clickedButton.querySelector('span');
          if (backButtonSpan) {
            // Check if we're in a level-2 view
            const isLevelTwoView = document.querySelector('.main-nav__level-two__sub-menu-wrapper')?.classList.contains('is-active');
            if (isLevelTwoView) {
              backButtonSpan.textContent = 'Zurück';
            } else {
              // For other cases, show the full "Zurück zu [title]" text
              const firstLevelTitle = document.querySelector('.main-nav__level-one__item.has-sub-menu.sub-menu-active')?.querySelector('button[aria-expanded="true"]')?.textContent?.trim() || '';
              backButtonSpan.textContent = `Zurück zu ${firstLevelTitle}`;
            }
          }
        }
      }
    }
  }

  /**
   * Resets the mobile navigation state when it is closed.
   * This function toggles the navigation wrapper scroll, resets the active third, second, and first level navigation items,
   * and removes the 'no-scroll' class from the navigation wrapper if it exists.
   */
  const resetNavigation = () => {
    if (!isDesktop) {
      toggleNavWrapperScroll();
      resetActiveThirdLevel();
    }
    resetActiveSecondLevel();
    resetActiveFirstLevel();
  };

  const initListeners = () => {
    mainNavButtons.forEach((navButton) => {
      const onClick = (e: Event) => {
        e.preventDefault();
        onNavButtonClicked(navButton);
      };

      navButton.addEventListener('click', onClick);
    });

    backButtons?.forEach((backButton) => {
      const onClick = (e: Event) => {
        e.preventDefault();
        onBackButtonClicked(backButton as HTMLButtonElement);
      };
      backButton.addEventListener('click', onClick);
    });

    /**
     * Listen to click events on the mobile navigation toggle button by
     * observing class changes in the "header__nav__wrapper" element.
     * */
    const mobileObserver = new MutationObserver((mutations) => {
      const wrapper = mutations[0].target as HTMLDivElement;

      if (!wrapper.classList.contains('header__nav__wrapper--open')) {
        resetNavigation();
      }
    });
    mobileObserver.observe(mainNavWrapper as Node, {
      attributes: true,
      attributeFilter: ['class'],
    });

    const handleClickOnDocument = (e: Event) => {
      if (!header.contains(e.target as Node)) {
        resetNavigation();
      }
    };
    document.addEventListener('click', handleClickOnDocument);
  };

  initListeners();

  if (isDesktop) {
    const makeFirstLevelItemsFocusableOnInit = () => {
      const firstLevelElements = mainNav.querySelectorAll(
        '.main-nav__level-one__item > a, .main-nav__level-one__item > button',
      );
      firstLevelElements.forEach((element) => {
        element.setAttribute('tabindex', '0');
      });
    };

    /**
     * Hide Teasers in second level flyout initially depending on the amount of second level items.
     * If there are three columns of second level items, the first teaser is hidden.
     * If there are four columns of second level items, the first and second teaser
     * are hidden.
     * */
    const initVisibilityOfSecondLevelTeasers = () => {
      const firstLevelElements = mainNav.querySelectorAll(
        '.main-nav__level-one__item',
      );
      firstLevelElements.forEach((firstLevel) => {
        const teasers = firstLevel.querySelectorAll(
          'a.main-nav__level-two__teaser',
        ) as NodeListOf<HTMLAnchorElement>;

        if (!teasers.length) return;

        const amountOfSecondLevelItems = firstLevel.querySelectorAll(
          'ul.main-nav__level-two li.main-nav__level-two__item:not(.main-nav__sub-menu__headline__parent-link)',
        ).length;

        if (amountOfSecondLevelItems > MAX_ITEMS_PER_COLUMN * 2) {
          const firstTeaser = teasers[0];
          firstTeaser.style.display = 'none';
        }

        if (
          amountOfSecondLevelItems > MAX_ITEMS_PER_COLUMN * 4 &&
          teasers.length > 1
        ) {
          teasers.forEach((teaser) => {
            teaser.style.display = 'none';
          })
        }
      });
    };

    makeFirstLevelItemsFocusableOnInit();
    initVisibilityOfSecondLevelTeasers();
  }
}
